<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :title="`Kelola Peserta - ${eventTitle}`"
    width="800px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <!-- Permission Info -->
    <el-alert
      v-if="!canEdit"
      title="Hanya Organizer yang dapat mengelola peserta"
      type="warning"
      :closable="false"
      style="margin-bottom: 15px;"
    >
    </el-alert>

    <!-- Add Participant Section -->
    <div v-if="canEdit && showAddForm" class="add-participant-form">
      <el-card shadow="never">
        <div slot="header">
          <span>Tambah Peserta Baru</span>
        </div>
        <el-form
          ref="participantForm"
          :model="participantForm"
          :rules="participantRules"
          label-width="120px"
        >
          <el-form-item label="Email" prop="email">
            <el-input
              v-model="participantForm.email"
              placeholder="user@domain.com"
              type="email"
            ></el-input>
          </el-form-item>
          <el-form-item label="Nama" prop="name">
            <el-input
              v-model="participantForm.name"
              placeholder="Nama peserta (optional)"
            ></el-input>
          </el-form-item>
          <el-form-item>
            <el-button
              type="primary"
              :loading="saving"
              @click="handleAddParticipant"
            >
              Tambah Peserta
            </el-button>
            <el-button @click="showAddForm = false">Batal</el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>

    <el-button
      v-if="canEdit && !showAddForm"
      type="primary"
      icon="el-icon-plus"
      size="small"
      style="margin-bottom: 15px;"
      @click="showAddForm = true"
    >
      Tambah Peserta
    </el-button>

    <!-- Participants List -->
    <div class="participants-list">
      <div v-if="loading" style="text-align: center; padding: 20px;">
        <i class="el-icon-loading"></i> Memuat peserta...
      </div>

      <el-empty
        v-else-if="participants.length === 0"
        description="Belum ada peserta"
        :image-size="100"
      ></el-empty>

      <el-table
        v-else
        :data="participants"
        border
        style="width: 100%"
      >
        <el-table-column
          type="index"
          label="No"
          width="50"
          align="center"
        ></el-table-column>

        <el-table-column label="Nama" min-width="150">
          <template slot-scope="scope">
            {{ scope.row.name || '-' }}
          </template>
        </el-table-column>

        <el-table-column label="Email" min-width="200">
          <template slot-scope="scope">
            {{ scope.row.email }}
          </template>
        </el-table-column>

        <el-table-column label="Status" width="120" align="center">
          <template slot-scope="scope">
            <el-tag
              :type="getStatusType(scope.row.status)"
              size="small"
            >
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Check-In" width="100" align="center">
          <template slot-scope="scope">
            <el-tag
              :type="scope.row.checkedIn ? 'success' : 'info'"
              size="small"
            >
              <i :class="scope.row.checkedIn ? 'el-icon-check' : 'el-icon-close'"></i>
              {{ scope.row.checkedIn ? 'Sudah' : 'Belum' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Response" width="150">
          <template slot-scope="scope">
            {{ scope.row.response || '-' }}
          </template>
        </el-table-column>

        <el-table-column
          v-if="canEdit"
          label="Aksi"
          width="100"
          align="center"
          fixed="right"
        >
          <template slot-scope="scope">
            <el-button
              type="danger"
              size="mini"
              icon="el-icon-delete"
              @click="handleRemoveParticipant(scope.row)"
            >
              Hapus
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">Tutup</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'ManageParticipantsDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    event: {
      type: Object,
      default: () => ({})
    },
    currentUserEmail: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      dialogVisible: false,
      participants: [],
      showAddForm: false,
      participantForm: {
        email: '',
        name: ''
      },
      participantRules: {
        email: [
          { required: true, message: 'Email wajib diisi', trigger: 'blur' },
          { type: 'email', message: 'Format email tidak valid', trigger: 'blur' }
        ]
      },
      saving: false,
      loading: false
    };
  },
  computed: {
    eventTitle() {
      return this.event?.subject || 'Event';
    },
    canEdit() {
      // Only organizer can manage participants
      return this.event?.organizerEmail === this.currentUserEmail;
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val;
      if (val) {
        this.loadParticipants();
      }
    },
    dialogVisible(val) {
      if (!val) {
        this.$emit('update:visible', false);
        this.showAddForm = false;
        this.resetForm();
      }
    }
  },
  methods: {
    ...mapActions('integration', ['updateEvent']),

    loadParticipants() {
      if (!this.event?._rawEvent?.attendees) {
        this.participants = [];
        return;
      }

      // Handle both response formats:
      // 1. Backend format: {name, email, responseStatus, checkedIn}
      // 2. Microsoft Graph format: {emailAddress: {name, address}, status: {response}}
      this.participants = this.event._rawEvent.attendees.map(attendee => {
        // Check if it's backend format (has email field directly)
        if (attendee.email) {
          return {
            name: attendee.name || '',
            email: attendee.email || '',
            status: (attendee.responseStatus || 'none').toLowerCase(),
            response: attendee.responseStatus || 'None',
            type: attendee.type || 'required',
            checkedIn: attendee.checkedIn || false
          };
        }
        // Microsoft Graph format
        return {
          name: attendee.emailAddress?.name || '',
          email: attendee.emailAddress?.address || '',
          status: attendee.status?.response || 'none',
          response: attendee.status?.response || '-',
          type: attendee.type || 'required'
        };
      });

      console.log('Loaded participants:', this.participants); // Debug log
    },

    async handleAddParticipant() {
      this.$refs.participantForm.validate(async (valid) => {
        if (!valid) return;

        if (!this.canEdit) {
          this.$message.error('Hanya organizer yang dapat mengelola peserta');
          return;
        }

        this.saving = true;
        try {
          // Get current attendees
          const currentAttendees = this.event._rawEvent?.attendees || [];
          
          // Check if email already exists (handle both formats)
          const emailExists = currentAttendees.some(a => {
            const email = a.email || a.emailAddress?.address;
            return email === this.participantForm.email;
          });
          
          if (emailExists) {
            this.$message.warning('Email peserta sudah ada dalam daftar');
            return;
          }

          // Create updated attendees array (extract emails from both formats)
          const updatedAttendees = [
            ...currentAttendees.map(a => a.email || a.emailAddress?.address),
            this.participantForm.email
          ];

          // Prepare update payload
          const updatePayload = {
            subject: this.event.subject,
            body: this.event._rawEvent.body?.content || this.event._rawEvent.body || '',
            bodyType: 'HTML',
            startDateTime: this.event.startDateTime,
            endDateTime: this.event.endDateTime,
            timeZone: 'Asia/Jakarta',
            location: this.event.location || '',
            attendees: updatedAttendees,
            isAllDay: this.event._rawEvent.isAllDay || false,
            isOnlineMeeting: this.event.isOnlineMeeting || false,
            onlineMeetingProvider: 'teamsForBusiness',
            reminderMinutesBeforeStart: this.event._rawEvent.reminderMinutesBeforeStart || 15,
            showAs: this.event._rawEvent.showAs || 'Busy'
          };

          // Call update event
          await this.updateEvent({
            userIdOrUpn: this.currentUserEmail,
            eventId: this.event.id,
            eventData: updatePayload
          });

          this.$message.success('Peserta berhasil ditambahkan!');
          
          // Emit event to refresh parent
          this.$emit('participant-updated');
          
          // Reset form and hide add form
          this.resetForm();
          this.showAddForm = false;

          // Reload participants after short delay
          setTimeout(() => {
            this.loadParticipants();
          }, 1000);

        } catch (error) {
          console.error('Error adding participant:', error);
          if (error.response?.status === 403) {
            this.$message.error('Anda tidak memiliki izin untuk mengubah event ini');
          } else {
            this.$message.error('Gagal menambahkan peserta: ' + (error.message || 'Unknown error'));
          }
        } finally {
          this.saving = false;
        }
      });
    },

    handleRemoveParticipant(participant) {
      this.$confirm(
        `Apakah Anda yakin ingin menghapus "${participant.name || participant.email}" dari daftar peserta?`,
        'Konfirmasi Hapus',
        {
          confirmButtonText: 'Ya, Hapus',
          cancelButtonText: 'Batal',
          type: 'warning'
        }
      ).then(async () => {
        try {
          // Get current attendees and remove this one (handle both formats)
          const currentAttendees = this.event._rawEvent?.attendees || [];
          const updatedAttendees = currentAttendees
            .filter(a => {
              const email = a.email || a.emailAddress?.address;
              return email !== participant.email;
            })
            .map(a => a.email || a.emailAddress?.address);

          // Prepare update payload
          const updatePayload = {
            subject: this.event.subject,
            body: this.event._rawEvent.body?.content || this.event._rawEvent.body || '',
            bodyType: 'HTML',
            startDateTime: this.event.startDateTime,
            endDateTime: this.event.endDateTime,
            timeZone: 'Asia/Jakarta',
            location: this.event.location || '',
            attendees: updatedAttendees,
            isAllDay: this.event._rawEvent.isAllDay || false,
            isOnlineMeeting: this.event.isOnlineMeeting || false,
            onlineMeetingProvider: 'teamsForBusiness',
            reminderMinutesBeforeStart: this.event._rawEvent.reminderMinutesBeforeStart || 15,
            showAs: this.event._rawEvent.showAs || 'Busy'
          };

          // Call update event
          await this.updateEvent({
            userIdOrUpn: this.currentUserEmail,
            eventId: this.event.id,
            eventData: updatePayload
          });

          this.$message.success('Peserta berhasil dihapus!');
          
          // Emit event to refresh parent
          this.$emit('participant-updated');

          // Reload participants after short delay
          setTimeout(() => {
            this.loadParticipants();
          }, 1000);

        } catch (error) {
          console.error('Error removing participant:', error);
          if (error.response?.status === 403) {
            this.$message.error('Anda tidak memiliki izin untuk mengubah event ini');
          } else {
            this.$message.error('Gagal menghapus peserta: ' + (error.message || 'Unknown error'));
          }
        }
      }).catch(() => {
        // User cancelled
      });
    },

    resetForm() {
      this.participantForm = {
        email: '',
        name: ''
      };
      if (this.$refs.participantForm) {
        this.$refs.participantForm.clearValidate();
      }
    },

    handleClose() {
      this.dialogVisible = false;
    },

    getStatusType(status) {
      const normalizedStatus = (status || 'none').toLowerCase();
      const statusMap = {
        accepted: 'success',
        tentative: 'warning',
        declined: 'danger',
        none: 'info'
      };
      return statusMap[normalizedStatus] || 'info';
    },

    getStatusText(status) {
      const normalizedStatus = (status || 'none').toLowerCase();
      const textMap = {
        accepted: 'Diterima',
        tentative: 'Mungkin',
        declined: 'Ditolak',
        none: 'Belum Respon'
      };
      return textMap[normalizedStatus] || status;
    }
  }
};
</script>

<style scoped>
.add-participant-form {
  margin-bottom: 20px;
}

.participants-list {
  margin-top: 15px;
}
</style>
